import { useEffect, useRef, useState } from 'react';
import { bus, subscribeWsState, wsClient, type WSConnectionState } from '@/lib/ws';
import { useStreamStore, type StreamEvent } from '@/stores/useStreamStore';
import { useTelemetryStore } from '@/stores/useTelemetryStore';
import { useFlowStore } from '@/stores/useFlowStore';
import type { EventMap } from '@tiw/shared-types';

/**
 * useRealtimeStream
 *
 * Bridges the shared Bus (fed by the WSClient) into the Zustand stores. On
 * mount it opens the connection (idempotent — the client no-ops if already
 * connected), subscribes to every channel via the bus wildcard, and tracks
 * connection state for the HUD.
 *
 * See packages/data-streams and docs/EVENT_ARCHITECTURE.md.
 */
export function useRealtimeStream(): WSConnectionState {
  const [state, setState] = useState<WSConnectionState>('idle');
  const push = useStreamStore((s) => s.push);
  const setTelemetry = useTelemetryStore((s) => s.setSample);
  const eventsThisSecond = useRef(0);
  const lastRateTick = useRef(performance.now());

  useEffect(() => {
    const unsubState = subscribeWsState((s) => {
      setState(s);
      useTelemetryStore.getState().setSample({ wsState: s });
    });
    const sub = bus.on('*', (event) => {
      const envelope = event as EventMap[keyof EventMap];
      const tsMs = Date.parse(envelope.ts);
      eventsThisSecond.current += 1;

      // The flow tick is continuous telemetry (2 Hz) — it drives the arcs via
      // the flow store and is kept out of the discrete event log below. Real
      // incidents arrive via the `useIncidents` poll, not this WS stream.
      if (envelope.channel === 'tiw.flow.tick') {
        useFlowStore.getState().apply(envelope.payload.samples);
      }

      // Discrete events (topology updates, bgp, dns) feed the event log; the
      // flow heartbeat is excluded so the log stays meaningful.
      if (envelope.channel !== 'tiw.flow.tick') {
        const stored: StreamEvent = {
          id: envelope.id,
          channel: envelope.channel,
          ts: tsMs,
          payload: envelope.payload,
        };
        push(stored);
      }

      const now = performance.now();
      const elapsed = now - lastRateTick.current;
      if (elapsed >= 1000) {
        setTelemetry({ eventsPerSec: (eventsThisSecond.current * 1000) / elapsed });
        eventsThisSecond.current = 0;
        lastRateTick.current = now;
      }
      setTelemetry({ wsLagMs: Math.max(0, Date.now() - tsMs) });
    });

    wsClient.connect();

    return () => {
      unsubState();
      sub.unsubscribe();
    };
  }, [push, setTelemetry]);

  return state;
}
